import { useEffect } from "react";

import { CopyId } from "../components/CopyId";
import { Note } from "../components/Note";
import { TicketCard } from "../components/TicketCard";
import { TICKETS_POLL_MS, useTickets } from "../hooks/useTickets";
import { usePolling } from "../hooks/usePolling";
import { t } from "../i18n/strings";
import type { TicketsFilter } from "../api/endpoints";

const FILTERS: { value: TicketsFilter; label: "tickets.filter.open" | "tickets.filter.closed" | "tickets.filter.any" }[] =
  [
    { value: "open", label: "tickets.filter.open" },
    { value: "closed", label: "tickets.filter.closed" },
    { value: "any", label: "tickets.filter.any" },
  ];

interface TicketsScreenProps {
  onOpen: (ticketId: number) => void;
  onOpenCount: (count: number) => void;
}

export function TicketsScreen({ onOpen, onOpenCount }: TicketsScreenProps) {
  const tickets = useTickets();

  // the list is the one screen that goes stale on its own: a client writes and nobody clicked
  // anything here. Polling stops while the tab is hidden, see usePolling
  usePolling(tickets.refresh, TICKETS_POLL_MS);

  // reported up in an effect and not during render: the parent turns it into state, and
  // setting a parent's state while rendering a child is how a render loop starts
  useEffect(() => {
    onOpenCount(tickets.openCount);
  }, [onOpenCount, tickets.openCount]);

  return (
    <section className="screen">
      <div className="filters">
        {FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            className={`filters__chip${tickets.filter === filter.value ? " filters__chip--active" : ""}`}
            onClick={() => tickets.setFilter(filter.value)}
          >
            {t(filter.label)}
            {filter.value === "open" && tickets.openCount > 0 ? (
              <span className="filters__badge">{tickets.openCount}</span>
            ) : null}
          </button>
        ))}
      </div>

      {tickets.userId !== undefined ? (
        <div className="filters">
          <span className="chip">{t("tickets.filterByUser")}</span>
          <CopyId value={tickets.userId} />
          <button type="button" className="button button--quiet" onClick={() => tickets.setUserId(undefined)}>
            {t("tickets.clearUser")}
          </button>
        </div>
      ) : null}

      {tickets.error ? <Note kind="error">{tickets.error}</Note> : null}

      {tickets.loading ? (
        <p className="screen__loading">{t("tickets.loading")}</p>
      ) : tickets.tickets.length === 0 ? (
        <p className="screen__empty">{t("tickets.empty")}</p>
      ) : (
        <div className="ticket-list">
          {tickets.tickets.map((ticket) => (
            <TicketCard key={ticket.id} ticket={ticket} onOpen={onOpen} />
          ))}
        </div>
      )}

      {tickets.cursor ? (
        <button type="button" className="button" disabled={tickets.loadingMore} onClick={tickets.loadMore}>
          {t("tickets.more")}
        </button>
      ) : null}
    </section>
  );
}
